import React from 'react';
import {
  Play, Eye, ChevronDown, ChevronUp, Clock, BookOpen, Layers,
  Award, Brain, Calendar, Sparkles, ArrowUpRight,
} from 'lucide-react';
import {
  getProgress,
  getCompletedLessonCount,
  getInstructorName,
  resolveCourseThumbnail,
  formatCourseDuration,
  formatCategoryLabel,
} from './studentPortalUtils';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

const ProgressRing = ({ pct, color, size = 44 }) => {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  return (
    <svg width={size} height={size} className="my-course-card__ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="my-course-card__ring-text">
        {pct}%
      </text>
    </svg>
  );
};

const MyCourseCard = ({
  enrollment,
  color,
  categoryIcon,
  isExpanded,
  onToggleDetails,
  onContinue,
}) => {
  const course = enrollment.course || {};
  const progress = getProgress(enrollment);
  const completed = getCompletedLessonCount(enrollment);
  const totalLessons = course.totalLessons || 0;
  const isCompleted = enrollment.status === 'completed';
  const instructor = getInstructorName(course);
  const thumbUrl = resolveCourseThumbnail(course, API_BASE);
  const emojiThumb = course.thumbnail?.length <= 4 && course.thumbnail && !course.thumbnail.includes('/');
  const isAI = course.metadata?.generatedBy || course.metadata?.pathId || course.aiGenerated;

  const lastAccess = enrollment.progress?.lastAccessedDate
    || enrollment.enrollmentDate
    || enrollment.createdAt;

  return (
    <article className={`my-course-card ${isExpanded ? 'my-course-card--expanded' : ''} ${isCompleted ? 'my-course-card--done' : ''}`}>
      <button
        type="button"
        className="my-course-card__cover"
        onClick={onContinue}
        aria-label={`Open ${course.title}`}
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="my-course-card__img" loading="lazy" />
        ) : (
          <div className="my-course-card__cover-fallback" style={{ background: `linear-gradient(135deg, ${color}55 0%, ${color}22 50%, rgba(9,9,11,0.9) 100%)` }}>
            <span className="my-course-card__cover-emoji">
              {emojiThumb ? course.thumbnail : <span className="my-course-card__cover-icon">{categoryIcon}</span>}
            </span>
          </div>
        )}
        <div className="my-course-card__cover-overlay" />
        <div className="my-course-card__cover-top">
          <span className="my-course-card__category">{formatCategoryLabel(course.category)}</span>
          {isAI && (
            <span className="my-course-card__ai-badge">
              <Sparkles size={11} /> AI
            </span>
          )}
        </div>
        <div className="my-course-card__cover-ring">
          <ProgressRing pct={progress} color={isCompleted ? '#4ade80' : color} />
        </div>
        {!isCompleted && progress > 0 && progress < 100 && (
          <span className="my-course-card__resume-hint">
            <Play size={12} fill="currentColor" /> Resume
          </span>
        )}
      </button>

      <div className="my-course-card__body">
        <div className="my-course-card__head">
          <div className="my-course-card__titles">
            <h4 className="my-course-card__title">{course.title}</h4>
            {course.tagline && <p className="my-course-card__instructor">{course.tagline}</p>}
            <p className="my-course-card__instructor">{instructor}</p>
          </div>
          <span className={`my-course-card__status ${isCompleted ? 'my-course-card__status--done' : ''}`}>
            {isCompleted ? 'Done' : 'Active'}
          </span>
        </div>

        <div className="my-course-card__chips">
          <span><Layers size={12} /> {course.level || 'All levels'}</span>
          <span><BookOpen size={12} /> {totalLessons || '—'} lessons</span>
          <span><Clock size={12} /> {formatCourseDuration(course.totalDuration)}</span>
        </div>

        <div className="my-course-card__progress">
          <div className="my-course-card__progress-meta">
            <span>{completed}/{totalLessons || '—'} lessons</span>
            <strong style={{ color: isCompleted ? '#4ade80' : color }}>{progress}%</strong>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%`, background: isCompleted ? '#4ade80' : color }}
            />
          </div>
        </div>

        <p className="my-course-card__next">
          {isCompleted
            ? `Completed ${enrollment.completion?.completionDate
              ? new Date(enrollment.completion.completionDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
              : ''}`
            : progress === 0
              ? 'Ready to start — open your first lesson'
              : `Pick up where you left off`}
        </p>

        <div className="my-course-card__actions">
          <button
            type="button"
            className="btn btn-primary my-course-card__cta"
            style={{ background: isCompleted ? undefined : color }}
            onClick={onContinue}
          >
            {isCompleted ? <Eye size={16} /> : <Play size={16} fill="currentColor" />}
            {isCompleted ? 'Review course' : 'Continue'}
            <ArrowUpRight size={14} className="my-course-card__cta-arrow" />
          </button>
          <button
            type="button"
            className={`my-course-card__details-btn ${isExpanded ? 'active' : ''}`}
            onClick={onToggleDetails}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Details
          </button>
        </div>

        {isExpanded && (
          <div className="my-course-card__panel">
            {course.description && (
              <p className="my-course-card__desc">{course.description}</p>
            )}
            {course.skills?.length > 0 && (
              <div className="my-course-card__skills">
                <span className="my-course-card__skills-label"><Brain size={13} /> Skills</span>
                <div className="my-course-card__skill-tags">
                  {course.skills.slice(0, 6).map((skill, idx) => (
                    <span key={idx} className="my-course-card__skill-tag" style={{ borderColor: `${color}40`, color }}>
                      {skill.name || skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="my-course-card__enrolled">
              <Calendar size={13} />
              Enrolled {new Date(enrollment.enrollmentDate || enrollment.createdAt).toLocaleDateString()}
              {lastAccess && (
                <> · Last active {new Date(lastAccess).toLocaleDateString()}</>
              )}
            </div>
            {isCompleted && (
              <div className="my-course-card__cert">
                <Award size={14} />
                Certificate eligible — view in Certificates tab
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default MyCourseCard;